import {
  doc,
  setDoc,
  getDocs,
  query,
  where,
  orderBy,
  updateDoc,
  deleteDoc,
} from 'firebase/firestore';
import { db } from '../firebase/config/firebase';
import { demandRequestsCol } from '../firebase/firestore/collections';
import { DemandRequest, DemandRequestStatus, TripType } from '../types';
import { studentDemandService } from './studentDemandService';

const STORAGE_KEY = 'oto_dev_demand_requests';

function getLocalDemandRequests(): DemandRequest[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveLocalDemandRequests(list: DemandRequest[]) {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {
    // Ignored
  }
}

export interface SubmitDemandRequestInput {
  studentId: string;
  studentSnapshot: {
    fullName: string;
    email: string;
    phoneNumber?: string;
  };
  universityId: string;
  universityName: string;
  governorate: string;
  date: string; // YYYY-MM-DD
  preferredDepartureTime: string;
  preferredArrivalTime?: string;
  tripType: TripType;
  pickupLocation?: string;
  notes?: string;
}

let cachedDemandRequests: { data: DemandRequest[]; timestamp: number } | null = null;

export const invalidateDemandRequestsCache = () => {
  cachedDemandRequests = null;
};

export const demandRequestService = {
  invalidateCache: invalidateDemandRequestsCache,

  /**
   * Submit a new demand request by a student
   */
  async submitDemandRequest(input: SubmitDemandRequestInput): Promise<{ success: boolean; data?: DemandRequest; error?: string }> {
    try {
      const docRef = doc(demandRequestsCol);
      const randomSuffix = Math.floor(1000 + Math.random() * 9000);
      const requestCode = `DMD-${randomSuffix}`;
      const nowIso = new Date().toISOString();

      const newDemandRequest: DemandRequest = {
        id: docRef.id,
        requestCode,
        studentId: input.studentId,
        studentSnapshot: input.studentSnapshot,
        universityId: input.universityId,
        universityName: input.universityName,
        governorate: input.governorate,
        pickupLocation: input.pickupLocation || '',
        date: input.date,
        preferredDepartureTime: input.preferredDepartureTime,
        preferredArrivalTime: input.preferredArrivalTime || '',
        tripType: input.tripType,
        notes: input.notes?.trim() || '',
        status: 'PENDING',
        matchedTripId: null,
        createdAt: nowIso,
        updatedAt: nowIso,
      };

      try {
        await setDoc(docRef, newDemandRequest);
      } catch (firestoreErr) {
        console.warn('Falling back to local session for demand request submission:', firestoreErr);
      }

      // Also persist to dev session
      const local = getLocalDemandRequests();
      local.unshift(newDemandRequest);
      saveLocalDemandRequests(local);

      // Mirror into studentDemand records so it displays in student demand section
      try {
        await studentDemandService.createDemandRecord(
          {
            studentName: input.studentSnapshot.fullName,
            phone: input.studentSnapshot.phoneNumber || '01000000000',
            universityId: input.universityId || `uni_${Date.now()}`,
            universityName: input.universityName,
            universityGovernorate: input.governorate,
            areaId: 'alex_custom',
            areaName: input.pickupLocation || 'الإسكندرية',
            preferredDepartureTime: input.preferredDepartureTime,
            notes: `طلب رحلة مباشرة (${newDemandRequest.requestCode}) - تاريخ ${input.date} - ${input.notes || ''}`.trim(),
          },
          {
            uid: input.studentId,
            email: input.studentSnapshot.email,
            name: input.studentSnapshot.fullName,
          },
          { skipDuplicateCheck: true }
        );
      } catch (mirrorErr) {
        console.warn('Mirroring demand request to studentDemand failed:', mirrorErr);
      }

      return { success: true, data: newDemandRequest };
    } catch (err: any) {
      console.error('Failed to submit demand request:', err);
      return { success: false, error: err.message || 'حدث خطأ أثناء تقديم طلب الرحلة' };
    }
  },

  /**
   * Get all demand requests submitted by a specific student
   */
  async getStudentDemandRequests(studentId: string): Promise<DemandRequest[]> {
    const list: DemandRequest[] = [];
    try {
      const q = query(
        demandRequestsCol,
        where('studentId', '==', studentId),
        orderBy('createdAt', 'desc')
      );
      const snap = await getDocs(q);
      snap.forEach((d) => {
        list.push({ ...d.data(), id: d.id } as DemandRequest);
      });
    } catch (err) {
      console.warn('Could not query Firestore for student demand requests, reading local:', err);
    }

    // Merge with local requests
    const local = getLocalDemandRequests().filter((r) => r.studentId === studentId);
    for (const item of local) {
      if (!list.some((existing) => existing.id === item.id)) {
        list.push(item);
      }
    }

    return list.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  },

  /**
   * Cancel or remove a pending demand request by student
   */
  async cancelDemandRequest(requestId: string): Promise<boolean> {
    try {
      try {
        const ref = doc(db, 'demandRequests', requestId);
        await updateDoc(ref, {
          status: 'REJECTED',
          updatedAt: new Date().toISOString(),
          studentCancelled: true,
        });
      } catch {
        // Ignored if local
      }

      const local = getLocalDemandRequests();
      const updated = local.map((r) => (r.id === requestId ? { ...r, status: 'REJECTED' as DemandRequestStatus } : r));
      saveLocalDemandRequests(updated);

      return true;
    } catch (err) {
      console.error('Failed to cancel demand request:', err);
      return false;
    }
  },

  /**
   * Admin: Get all demand requests across all students with instant memory caching
   */
  async getAllDemandRequests(forceRefresh = false): Promise<DemandRequest[]> {
    if (!forceRefresh && cachedDemandRequests && Date.now() - cachedDemandRequests.timestamp < 25000) {
      return cachedDemandRequests.data;
    }

    const list: DemandRequest[] = [];
    try {
      const snap = await getDocs(demandRequestsCol);
      snap.forEach((d) => {
        list.push({ ...d.data(), id: d.id } as DemandRequest);
      });
    } catch (err) {
      console.warn('Could not read demand requests from Firestore, using local:', err);
    }

    // Merge with local
    const local = getLocalDemandRequests();
    for (const item of local) {
      if (!list.some((existing) => existing.id === item.id)) {
        list.push(item);
      }
    }

    const sorted = list.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    cachedDemandRequests = { data: sorted, timestamp: Date.now() };
    return sorted;
  },

  /**
   * Admin: Update status of a demand request
   */
  async updateDemandRequestStatus(
    requestId: string,
    status: DemandRequestStatus,
    matchedTripId?: string | null,
    adminNote?: string
  ): Promise<boolean> {
    try {
      const nowIso = new Date().toISOString();
      try {
        const ref = doc(db, 'demandRequests', requestId);
        await updateDoc(ref, {
          status,
          matchedTripId: matchedTripId || null,
          adminNote: adminNote || '',
          updatedAt: nowIso,
        });
      } catch {
        // Ignored
      }

      const local = getLocalDemandRequests();
      const updated = local.map((r) =>
        r.id === requestId
          ? {
              ...r,
              status,
              matchedTripId: matchedTripId !== undefined ? matchedTripId : r.matchedTripId,
              notes: adminNote ? `${r.notes || ''} [ملاحظة الإدارة: ${adminNote}]` : r.notes,
              updatedAt: nowIso,
            }
          : r
      );
      saveLocalDemandRequests(updated);

      return true;
    } catch (err) {
      console.error('Failed to update demand request status:', err);
      return false;
    }
  },

  /**
   * Admin: Delete a demand request
   */
  async deleteDemandRequest(requestId: string): Promise<boolean> {
    try {
      try {
        const ref = doc(db, 'demandRequests', requestId);
        await deleteDoc(ref);
      } catch (_) {}
      const local = getLocalDemandRequests().filter((r) => r.id !== requestId);
      saveLocalDemandRequests(local);
      return true;
    } catch (err) {
      console.error('Failed to delete demand request:', err);
      return false;
    }
  },

  /**
   * Admin: Seed high-quality operational demand samples for testing
   */
  async seedSampleDemandRequests(): Promise<DemandRequest[]> {
    const today = new Date();
    const formatDate = (offsetDays: number) => {
      const d = new Date();
      d.setDate(today.getDate() + offsetDays);
      return d.toISOString().split('T')[0];
    };

    const samples: SubmitDemandRequestInput[] = [
      {
        studentId: 'std_demo_1',
        studentSnapshot: {
          fullName: 'زياد محمد إبراهيم',
          email: 'ziad.demo@gmail.com',
          phoneNumber: '01019283746',
        },
        universityId: 'uni_tanta',
        universityName: 'جامعة طنطا',
        governorate: 'الغربية',
        date: formatDate(2),
        preferredDepartureTime: '06:30',
        preferredArrivalTime: '08:00',
        tripType: 'ROUND_TRIP',
        pickupLocation: 'ميدان محطة مصر (بجوار المحطة المركزية)',
        notes: 'مطلوب ميعاد العودة 04:30 عصراً بعد المحاضرات',
      },
      {
        studentId: 'std_demo_2',
        studentSnapshot: {
          fullName: 'مريم حسام الدين',
          email: 'mariam.demo@gmail.com',
          phoneNumber: '01128394857',
        },
        universityId: 'uni_tanta',
        universityName: 'جامعة طنطا',
        governorate: 'الغربية',
        date: formatDate(2),
        preferredDepartureTime: '06:30',
        preferredArrivalTime: '08:00',
        tripType: 'ROUND_TRIP',
        pickupLocation: 'محطة سيدي جابر (أمام النفق)',
        notes: 'كلية الصيدلة - مجمع الكليات الطبية',
      },
      {
        studentId: 'std_demo_3',
        studentSnapshot: {
          fullName: 'عبدالرحمن خالد النجار',
          email: 'abdo.demo@gmail.com',
          phoneNumber: '01298473625',
        },
        universityId: 'uni_tanta',
        universityName: 'جامعة طنطا',
        governorate: 'الغربية',
        date: formatDate(2),
        preferredDepartureTime: '07:00',
        preferredArrivalTime: '08:30',
        tripType: 'ONE_WAY',
        pickupLocation: 'كوبري محرم بك (أسفل الكوبري الدولي)',
        notes: 'سفر صباحي فقط',
      },
      {
        studentId: 'std_demo_4',
        studentSnapshot: {
          fullName: 'نوران إيهاب السعيد',
          email: 'nouran.demo@gmail.com',
          phoneNumber: '01092837465',
        },
        universityId: 'uni_kafr_elsheikh',
        universityName: 'جامعة كفر الشيخ',
        governorate: 'كفر الشيخ',
        date: formatDate(3),
        preferredDepartureTime: '06:00',
        preferredArrivalTime: '08:00',
        tripType: 'ROUND_TRIP',
        pickupLocation: 'الموقف الجديد (موقف المحافظات)',
        notes: 'طبيبة امتياز - يوم تدريب كامل',
      },
      {
        studentId: 'std_demo_5',
        studentSnapshot: {
          fullName: 'محمود أحمد عاشور',
          email: 'ashour.demo@gmail.com',
          phoneNumber: '01583746291',
        },
        universityId: 'uni_kafr_elsheikh',
        universityName: 'جامعة كفر الشيخ',
        governorate: 'كفر الشيخ',
        date: formatDate(3),
        preferredDepartureTime: '06:30',
        preferredArrivalTime: '08:15',
        tripType: 'ROUND_TRIP',
        pickupLocation: 'ميدان فيكتوريا (أمام المحطة)',
        notes: 'كلية الهندسة',
      },
      {
        studentId: 'std_demo_6',
        studentSnapshot: {
          fullName: 'شهد طارق عبدالحميد',
          email: 'shahd.demo@gmail.com',
          phoneNumber: '01064738291',
        },
        universityId: 'uni_sadat',
        universityName: 'جامعة مدينة السادات الأهلية',
        governorate: 'المنوفية',
        date: formatDate(4),
        preferredDepartureTime: '07:00',
        preferredArrivalTime: '08:45',
        tripType: 'ROUND_TRIP',
        pickupLocation: 'المندرة (أمام شارع النبوي المهندس)',
        notes: 'جامعة السادات الأهلية - طريق المحور',
      },
    ];

    const results: DemandRequest[] = [];
    for (const s of samples) {
      const res = await this.submitDemandRequest(s);
      if (res.data) results.push(res.data);
    }
    return results;
  },

  /**
   * Admin: Clear all local demand requests
   */
  clearAllLocalDemandRequests(): void {
    if (typeof window !== 'undefined') {
      try {
        sessionStorage.removeItem(STORAGE_KEY);
      } catch (_) {}
    }
  },
};
